import { createContext, useContext, ReactNode } from 'react';
import type { MobileDetailResponse } from '@/api/client-detail';
import type { ActiveVisit, CheckOutPayload } from '@/api/visits';

interface ClientContextValue {
  clientId: number;          // Real client ID for API calls
  visitTargetId: number;     // Visit target ID (may be virtual office ID for visit operations)
  officeId?: string;         // Office ID for additional offices (undefined = main office)
  detail: MobileDetailResponse | undefined;
  detailLoading: boolean;
  activeVisit: ActiveVisit | null;
  // Actions (use visitTargetId for visit operations)
  onCheckIn: (visitTargetId: number) => void;
  onCheckOut: (payload?: CheckOutPayload) => void;
  onMarkVisited: (visitTargetId: number, note?: string) => void;
  onMarkSkipped: (visitTargetId: number, note?: string) => void;
  onUndoCheckIn: (visitTargetId: number) => void;
  onSetUrgent: (visitTargetId: number) => void;
  onRemoveUrgent: (visitTargetId: number) => void;
  onPlanToday: (visitTargetId: number) => void;
  onCancelPlanToday: (visitTargetId: number) => void;
  onScheduleVisit: (visitTargetId: number) => void;
  isActionLoading: boolean;
}

const ClientContext = createContext<ClientContextValue | null>(null);

export function useClientContext() {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClientContext must be used within ClientProvider');
  }
  return context;
}

interface ClientProviderProps {
  value: ClientContextValue;
  children: ReactNode;
}

export function ClientProvider({ value, children }: ClientProviderProps) {
  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
}
